import { createAsyncThunk } from '@reduxjs/toolkit';

import { type AxiosResponse, isAxiosError } from 'axios';
import { ObjectToCamel } from 'ts-case-convert';
import { urlJoin, urlJoinP } from 'url-join-ts';
import { captureMessage } from '@sentry/nextjs';

import { createAxiosInstance } from '@app/common';
import { MAP_DEFAULTS } from '@app/consts';
import { HttpMethods, LocationType, LocationTypeEnum, SliceNameEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import { buildMapApiParams } from '@app/helpers/map-api-params.helpers';
import { convertGeoJsonToMarkers } from '@app/helpers/map-geojson-to-markers';
import {
    GeoBaseModel,
    LocationDetailsModel,
    MapAccommodationsRequest,
    MapAccommodationsResponse,
} from '@app/models';
import { GeoTileModel } from '@app/models/geo.tiles.model';
import { MapPolygonsBody, MapPolygonsResponse } from '@app/models/map-polygons.model';

const buildPolygonsUrl = (geoType: string, geoId: string) =>
    urlJoinP(environment.NOC_API_END_POINT, ['api', 'geo', 'tiling', 'polygons', geoType, geoId]);

export async function sendMapPolygons(
    geoId: string,
    geoType: LocationType,
    body: MapPolygonsBody,
    method: HttpMethods.POST | HttpMethods.PATCH,
): Promise<AxiosResponse | null> {
    const axios = createAxiosInstance();

    try {
        const url = buildPolygonsUrl(geoType, geoId);

        const response =
            method === HttpMethods.POST
                ? await axios.post(url, body)
                : await axios.patch(url, body);

        return response;
    } catch (error) {
        if (isAxiosError(error)) {
            captureMessage(
                `Failed to ${method} map polygons: ${error.response?.status} ${error.message}`,
            );
            return error.response ?? null;
        }

        captureMessage(`Failed to ${method} map polygons: ${error}`);
        throw error;
    }
}

export async function getMapPolygons(
    geoId: string,
    geoType: LocationType,
): Promise<MapPolygonsResponse | null> {
    const axios = createAxiosInstance();
    try {
        const response = await axios.get(buildPolygonsUrl(geoType, geoId));
        return response.data;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
    }
}

export async function getGeoTiles(
    geoId: string,
    locationType: LocationType,
    indexVersion?: number,
): Promise<GeoTileModel[] | null> {
    try {
        const axios = createAxiosInstance();
        const response = await axios.get(urlJoinP(environment.SSR_API_HOST, ['geo-tiles', '/']), {
            params: { geoId, locationType, indexVersion },
        });
        return response.data;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        captureMessage(`Failed to fetch geo tiles: ${error}`);
        throw error;
    }
}

export async function getGeoDetails<T extends ObjectToCamel<GeoBaseModel>>(
    geoId: string,
    locationType: LocationType,
): Promise<T | null> {
    try {
        const axios = createAxiosInstance();
        const response = await axios.get(urlJoinP(environment.SSR_API_HOST, ['geo-details', '/']), {
            params: { geoId, locationType },
        });
        return response.data;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        captureMessage(`Failed to fetch geo details: ${error}`);
        throw error;
    }
}

export async function getLocationDetails(
    geoId: string,
    locationType: LocationType,
): Promise<LocationDetailsModel | null> {
    try {
        const axios = createAxiosInstance();
        const response = await axios.get(
            urlJoinP(environment.SSR_API_HOST, ['location-details', '/']),
            {
                params: { geoId, locationType },
            },
        );
        return response.data;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        captureMessage(`Failed to fetch location details: ${error}`);
        throw error;
    }
}

// ---------------------------------------------------------------------------
// Temporary logging helpers: debug tiling performance, remove after phase 2
// ---------------------------------------------------------------------------
const ENABLE_LOGS = typeof window !== 'undefined' && localStorage.getItem('debug_user') === '1';

const logPerf = (label: string, start?: number, end?: number): void => {
    if (!ENABLE_LOGS) return;

    if (start !== undefined && end !== undefined) {
        console.log(`[PERF] ${label}: ${(end - start).toFixed(0)} ms`);
    } else {
        console.log(`[PERF] ${label}...`);
    }
};

const logCache = (tileResults: PromiseSettledResult<{ headers: any }>[]): void => {
    if (!ENABLE_LOGS) return;

    const cacheStats = { hit: 0, miss: 0, unknown: 0 };
    const sampleHeaders: unknown[] = [];

    tileResults.forEach(result => {
        if (result.status === 'fulfilled') {
            const { headers } = result.value;

            if (sampleHeaders.length < 3) sampleHeaders.push(headers);

            const cacheHeader =
                headers?.['x-cache'] || headers?.['X-Cache'] || headers?.['via'] || 'unknown';
            if (typeof cacheHeader === 'string') {
                if (cacheHeader.includes('HIT')) cacheStats.hit++;
                else if (cacheHeader.includes('MISS')) cacheStats.miss++;
                else cacheStats.unknown++;
            } else {
                cacheStats.unknown++;
            }
        }
    });

    console.log('[CACHE] Tiles:', {
        hit: cacheStats.hit,
        miss: cacheStats.miss,
        unknown: cacheStats.unknown,
        sampleHeaders,
    });
};
// ---------------------------------------------------------------------------
// End of temporary logging helpers
// ---------------------------------------------------------------------------

export const fetchMapAccommodations = createAsyncThunk<
    MapAccommodationsResponse,
    MapAccommodationsRequest
>(
    `${SliceNameEnum.Map}/fetch-accommodations`,
    async (
        {
            checkInDate,
            checkOutDate,
            adultsCount,
            childrenCount,
            childrenAges,
            priceFrom,
            priceTo,
            propertyTypeIds,
            amenityIds,
            provisionIds,
            roomEquipmentIds,
            reservationFeatures,
            rating,
            locationDetails,
            flexDates,
            skipSingleAccommodation,
            isOneAlgorithmMode,
        },
        { rejectWithValue },
    ) => {
        try {
            const axios = createAxiosInstance();
            const {
                location: { bounds, tilesBounds },
                accommodationId,
            } = locationDetails.details;

            if (!bounds) return rejectWithValue('No bounds available in location details');

            if (isOneAlgorithmMode && !tilesBounds) {
                return rejectWithValue('No tiles available in One-Algorithm-Mode');
            }

            const apiUrl = urlJoin(environment.NOC_API_END_POINT, 'api', 'map', 'accommodations');
            const paramsBase = {
                checkInDate,
                checkOutDate,
                adultsCount,
                childrenCount,
                childrenAges,
                priceFrom,
                priceTo,
                propertyTypeIds,
                amenityIds,
                provisionIds,
                roomEquipmentIds,
                reservationFeatures,
                rating,
                flexDates,
            };

            const paramsForBounds = buildMapApiParams({
                maxLat: bounds.maxLat,
                maxLon: bounds.maxLon,
                minLat: bounds.minLat,
                minLon: bounds.minLon,
                ...paramsBase,
            });

            let mergedGeoJson: GeoJSON.FeatureCollection;
            const totalStart = performance.now();

            async function fetchInBatches<T>(requests: Promise<T>[], batchSize = 30) {
                const results: PromiseSettledResult<T>[] = [];

                for (let i = 0; i < requests.length; i += batchSize) {
                    const batch = requests.slice(i, i + batchSize);
                    results.push(...(await Promise.allSettled(batch)));
                }

                return results;
            }

            if (tilesBounds && tilesBounds.length > 0) {
                // Tiling mode: fetch accommodations per tile
                const tilingStart = performance.now();
                logPerf(`Starting tiling (${tilesBounds.length} tiles)`);

                const isFulfilled = <T>(
                    input: PromiseSettledResult<T>,
                ): input is PromiseFulfilledResult<T> => input.status === 'fulfilled';

                const tileRequests = tilesBounds.map(tile => {
                    const params = buildMapApiParams({
                        ...paramsBase,
                        maxLat: tile.maxLat,
                        maxLon: tile.maxLon,
                        minLat: tile.minLat,
                        minLon: tile.minLon,
                    });
                    return axios
                        .get<GeoJSON.FeatureCollection>(apiUrl, { params })
                        .then(res => ({ data: res.data, headers: res.headers }));
                });

                const batchSize = MAP_DEFAULTS.BATCH_SIZE;
                const shouldBatch = isOneAlgorithmMode || tileRequests.length > batchSize;
                const tileResults = shouldBatch
                    ? await fetchInBatches(tileRequests, batchSize)
                    : await Promise.allSettled(tileRequests);

                logPerf(`Tiles fetched (${tilesBounds.length})`, tilingStart, performance.now());
                logCache(tileResults);

                const tileResponses = tileResults.filter(isFulfilled).map(r => r.value);

                const featuresMap = new Map<string | number, GeoJSON.Feature>();
                tileResponses
                    .flatMap(r => r.data.features)
                    .forEach(f => {
                        const id = f.properties?.id;
                        if (id && !featuresMap.has(id)) featuresMap.set(id, f);
                    });

                mergedGeoJson = { type: 'FeatureCollection', features: [...featuresMap.values()] };
            } else {
                // Legacy mode: single request for all accommodations
                const legacyStart = performance.now();
                logPerf('Starting legacy (single request)');

                try {
                    const response = await axios.get<GeoJSON.FeatureCollection>(apiUrl, {
                        params: paramsForBounds,
                    });
                    mergedGeoJson = response.data;
                } catch (apiError) {
                    if (isAxiosError(apiError)) {
                        console.warn(
                            `Map accommodations API returned status: ${apiError.response?.status}`,
                            apiError.message,
                        );
                    } else {
                        console.error('Error fetching map accommodations:', apiError);
                    }
                    return rejectWithValue('Failed to fetch map accommodations');
                }

                logPerf('Legacy request finished', legacyStart, performance.now());
            }

            // Handling a single accommodation object
            if (accommodationId && !skipSingleAccommodation) {
                const targetAccommodation = mergedGeoJson.features.find(
                    feature =>
                        feature.properties?.type === LocationTypeEnum.Accommodation &&
                        feature.properties?.id === accommodationId,
                );

                if (targetAccommodation && targetAccommodation.properties) {
                    targetAccommodation.properties.isInitiallySelected = true;
                } else {
                    const apiUrlAccommodation = `${apiUrl}/${accommodationId}`;
                    try {
                        const accommodation = await axios.get(apiUrlAccommodation, {
                            params: paramsForBounds,
                        });
                        const unavailableAccommodation = accommodation.data as GeoJSON.Feature;
                        if (unavailableAccommodation.properties) {
                            unavailableAccommodation.properties.isUnavailable = true;
                            unavailableAccommodation.properties.isInitiallySelected = true;
                        }
                        mergedGeoJson.features.push(unavailableAccommodation);
                    } catch (err) {
                        console.warn('Failed to fetch single accommodation', err);
                    }
                }
            }

            const accommodations = convertGeoJsonToMarkers(mergedGeoJson);

            logPerf(
                `Total fetch time (${tilesBounds?.length ? 'tiling' : 'legacy'})`,
                totalStart,
                performance.now(),
            );

            return { accommodations };
        } catch (err) {
            if (err instanceof Error) {
                return rejectWithValue(err.message);
            } else {
                return rejectWithValue('Failed to fetch map accommodations');
            }
        }
    },
);
